import { useLocation } from '@/services/router/compat';

// Matches the geoset id segment in `/geosets/:geosetId[/...]`. Geoset ids are
// numeric, so a digit run keeps this from matching the bare geosets index.
const GEOSET_ID_URL_REGEX = /\/geosets\/(\d+)/;

/** Parse the geoset id from a `/geosets/:geosetId` path; null when absent. */
export function getGeosetIdFromUrl(pathname: string): string | null {
  return pathname.match(GEOSET_ID_URL_REGEX)?.[1] ?? null;
}

/**
 * The geoset id from the current URL, or null on the geosets index (and any
 * route without a geoset segment). Mirrors {@link useWorkspaceIdFromCurrentUrl}
 * so the geofence pages — which read the workspace id the same way — can build
 * links back into their owning geoset.
 */
export function useGeosetIdFromCurrentUrl(): string | null {
  const location = useLocation();
  return getGeosetIdFromUrl(location.pathname);
}

/**
 * The geoset id from the current URL, asserted present. For pages that render
 * under `GeosetRequiredGuard` (the geoset-scoped geofence pages), which
 * short-circuits to the not-found page when the id is missing — so by the time
 * those pages render, the id is guaranteed. Throwing here lets callers read a
 * plain `string` instead of threading `?? ''`/null checks past the guard.
 */
export function useRequiredGeosetIdFromCurrentUrl(): string {
  const geosetId = useGeosetIdFromCurrentUrl();
  if (geosetId == null) {
    throw new Error('Expected a geoset id in the URL. This hook must run under GeosetRequiredGuard.');
  }
  return geosetId;
}
